import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowLeft, ShieldAlert, MessageCircle, Smartphone, FileX2, UserX, FileSearch, Scale, Gavel,
  CheckCircle2, Info, ClipboardList,
} from "lucide-react";
import { CLAIM_DESK, waHref } from "@/config/claimDesk";
import { ClaimHelpForm } from "@/components/claimhelp/ClaimHelpForm";

const WA_MESSAGE = "Hi, I have a problem with my insurance claim and I don't have an advisor. Can you help me?";

const FOR_YOU = [
  { icon: Smartphone, title: "You bought it online", desc: "On an insurer's website, an app, through a bank, or from an agent who no longer picks up." },
  { icon: FileX2, title: "Your claim went wrong", desc: "Rejected, only partly paid, stuck for weeks, or cashless was refused at the hospital." },
  { icon: UserX, title: "No one is on your side", desc: "You don't have an advisor to read the fine print or argue your case with the insurer." },
];

const STEPS = [
  { icon: ClipboardList, title: "Share your case", desc: "Fill the short form below and attach the rejection letter, policy copy or bills if you have them." },
  { icon: FileSearch, title: "Free first review", desc: "We read the insurer's reason against your policy wording and the IRDAI rules." },
  { icon: Scale, title: "Honest answer", desc: "We tell you plainly whether your case is worth fighting, and any fee, before you decide." },
  { icon: Gavel, title: "We fight it with you", desc: "Insurer grievance cell, then IRDAI Bima Bharosa, then the Insurance Ombudsman if needed." },
];

const STRAIGHT_TALK = [
  "We can't promise every claim will be paid. The final decision rests with the insurer, the Ombudsman or the courts.",
  "The first review is free. If we take up your case, any fee is agreed with you before we start.",
  "Our support is guidance and paperwork, not legal representation in court.",
  "Your documents are used only to review your claim.",
];

const Header = ({ navigate }) => (
  <header className="sticky top-0 z-40 border-b border-[#C9D4E6] bg-white/90 backdrop-blur-xl">
    <div className="mx-auto flex max-w-5xl items-center gap-3 px-4 py-3 sm:px-6">
      <button onClick={() => navigate("/")} className="-ml-2 rounded-lg p-2 hover:bg-[#EDF1F8]" data-testid="claim-help-back-btn" aria-label="Home"><ArrowLeft className="h-5 w-5" /></button>
      <button onClick={() => navigate("/")} data-testid="claim-help-logo"><img src="/kavachbox-logo-light-cropped.png" alt="Kavach Box" className="h-6 w-auto sm:h-7" /></button>
      <span className="ml-auto flex items-center gap-1.5 text-xs font-semibold text-rose-600"><ShieldAlert className="h-4 w-4" /> Claim Help</span>
    </div>
  </header>
);

const WaButton = ({ testId, className = "" }) => (
  <a href={waHref(CLAIM_DESK.whatsapp, WA_MESSAGE)} target="_blank" rel="noopener noreferrer" data-testid={testId}
    className={`inline-flex h-12 items-center justify-center gap-2 rounded-xl bg-green-600 px-6 text-sm font-bold text-white shadow-lg shadow-green-600/25 transition-[transform,background-color] hover:bg-green-700 active:scale-95 ${className}`}>
    <MessageCircle className="h-5 w-5" /> WhatsApp us
  </a>
);

const CardGrid = ({ items, cols, numbered, testId }) => (
  <div className={`grid gap-4 ${cols}`} data-testid={testId}>
    {items.map((it, i) => (
      <motion.div key={it.title} initial={{ opacity: 0, y: 14 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.08 }}
        className="glass-card p-5" data-testid={`${testId}-${i}`}>
        <div className="mb-3 flex items-center gap-2">
          <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-[#D4A017] to-[#D9A82C] shadow-lg shadow-[#D4A017]/15"><it.icon className="h-5 w-5 text-[#0D1A3A]" /></span>
          {numbered && <span className="text-xs font-bold text-[#A87608]">STEP {i + 1}</span>}
        </div>
        <h3 className="mb-1 font-bold">{it.title}</h3>
        <p className="text-sm leading-relaxed text-[#2E3B57]">{it.desc}</p>
      </motion.div>
    ))}
  </div>
);

const Success = ({ done, onAgain }) => (
  <div className="glass-card p-7 text-center" data-testid="claim-help-success">
    <span className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full border border-emerald-500/40 bg-emerald-500/15"><CheckCircle2 className="h-8 w-8 text-emerald-600" /></span>
    <h3 className="mb-2 text-xl font-bold">Thank you, {done.name.trim().split(" ")[0]}!</h3>
    <p className="mx-auto mb-5 max-w-md text-sm text-[#2E3B57]" data-testid="claim-help-success-text">We've received your case. Our team will review it and call you on +91 {done.mobile}.</p>
    <div className="flex flex-col justify-center gap-3 sm:flex-row">
      <WaButton testId="success-whatsapp-btn" />
      <button onClick={onAgain} className="h-12 rounded-xl border border-[#C9D4E6] px-6 text-sm font-semibold hover:bg-white" data-testid="claim-help-another-btn">Send another case</button>
    </div>
  </div>
);

export default function ClaimHelp() {
  const navigate = useNavigate();
  const [done, setDone] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    const prev = document.title;
    document.title = "Claim Rejected? We'll Help | Kavach Box";
    return () => { document.title = prev; };
  }, []);

  const toForm = () => document.getElementById("claim-help-form-section")?.scrollIntoView({ behavior: "smooth" });
  const finish = (d) => { setDone(d); toForm(); };

  return (
    <div className="min-h-screen bg-[#F4F6FA] text-[#1B2A4A]" data-testid="claim-help-page">
      <Header navigate={navigate} />
      <main className="mx-auto max-w-5xl px-4 pb-20 sm:px-6">
        <section className="pb-8 pt-10" data-testid="claim-help-hero">
          <motion.div initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            <span className="mb-5 inline-flex items-center gap-1.5 rounded-full bg-rose-500/10 px-3 py-1 text-xs font-semibold text-rose-600"><ShieldAlert className="h-4 w-4" /> For policies bought anywhere</span>
            <h1 className="mb-4 text-4xl font-bold leading-tight tracking-tight sm:text-5xl lg:text-6xl">
              Claim rejected? <span className="text-gradient-gold">You don't have to fight it alone.</span>
            </h1>
            <p className="mb-7 max-w-2xl text-base leading-relaxed text-[#2E3B57] md:text-lg">
              Bought your policy online and have no advisor to call? Share your case with us. We'll review it for free and tell you honestly what can be done.
            </p>
            <div className="flex flex-col gap-3 sm:flex-row">
              <button onClick={toForm} data-testid="hero-request-review-btn" className="inline-flex h-12 items-center justify-center gap-2 rounded-xl bg-[#D4A017] px-6 text-sm font-bold text-[#0A0F1A] shadow-lg shadow-[#D4A017]/25 transition-[transform,background-color] hover:bg-[#c1930f] active:scale-95">Request a free review</button>
              <WaButton testId="hero-whatsapp-btn" />
            </div>
          </motion.div>
        </section>

        <section className="py-8"><h2 className="mb-5 text-xl font-bold tracking-tight sm:text-2xl">Is this for you?</h2><CardGrid items={FOR_YOU} cols="md:grid-cols-3" testId="claim-help-for-you" /></section>
        <section className="py-8"><h2 className="mb-5 text-xl font-bold tracking-tight sm:text-2xl">How we help</h2><CardGrid items={STEPS} cols="sm:grid-cols-2 lg:grid-cols-4" numbered testId="claim-help-step" /></section>

        <section id="claim-help-form-section" className="scroll-mt-20 py-8" data-testid="claim-help-form-section">
          <h2 className="mb-2 text-xl font-bold tracking-tight sm:text-2xl">Request a free review</h2>
          <p className="mb-5 text-sm text-[#2E3B57]">Takes about 2 minutes. The more you share, the faster we can help.</p>
          {done ? <Success done={done} onAgain={() => setDone(null)} /> : <ClaimHelpForm onDone={finish} />}
        </section>

        <section className="py-8" data-testid="claim-help-straight-talk">
          <h2 className="mb-5 flex items-center gap-2 text-xl font-bold tracking-tight sm:text-2xl"><Info className="h-5 w-5 text-[#A87608]" /> Straight talk</h2>
          <div className="glass-card space-y-3 p-5 sm:p-6">
            {STRAIGHT_TALK.map((t) => <p key={t} className="flex gap-2.5 text-sm leading-relaxed text-[#2E3B57]"><span className="font-bold text-[#A87608]">•</span>{t}</p>)}
          </div>
        </section>
      </main>
      <footer className="border-t border-[#C9D4E6] bg-white px-4 py-8 text-center text-xs text-[#3A4557]">Kavach Box · A part of Shree Finserv Group</footer>
    </div>
  );
}
